/**
 * MUTAGIM.SHOP — Production Checklist Page (Phase 20)
 * Checklist complète pour le lancement en production
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft, CheckCircle, AlertTriangle, XCircle, 
  CreditCard, Mail, Package, Image, Globe, Shield,
  FileText, Zap, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Définition de la checklist complète
const CHECKLIST_ITEMS = [
  {
    category: "PAIEMENTS",
    icon: CreditCard,
    items: [
      { id: "stripe_live", label: "Stripe clé live configurée", critical: true },
      { id: "paypal_production", label: "PayPal credentials production", critical: true },
      { id: "bit_api", label: "Bit API key réelle", critical: false },
    ]
  },
  {
    category: "EMAILS",
    icon: Mail,
    items: [
      { id: "resend_api", label: "Resend API key réelle", critical: true },
      { id: "domain_verified", label: "Domaine vérifié (mutagim.shop)", critical: true },
      { id: "email_test", label: "Email test envoyé avec succès", critical: false },
    ]
  },
  {
    category: "AGENTS",
    icon: Package,
    items: [
      { id: "acbuy_credentials", label: "Credentials ACBUY production", critical: true },
      { id: "acbuy_balance", label: "Solde ACBUY initial rechargé", critical: true },
      { id: "acbuy_test_order", label: "Test commande réelle effectué", critical: false },
      { id: "kakobuy_configured", label: "KakoBuy configuré (optionnel)", critical: false },
    ]
  },
  {
    category: "IA & MÉDIAS",
    icon: Image,
    items: [
      { id: "replicate_api", label: "Replicate API key (images IA)", critical: false },
      { id: "17track_api", label: "17track API key réelle", critical: true },
      { id: "meta_pixel", label: "Meta Pixel ID configuré", critical: false },
      { id: "tiktok_pixel", label: "TikTok Pixel ID configuré", critical: false },
      { id: "ga4_id", label: "Google Analytics 4 ID configuré", critical: false },
    ]
  },
  {
    category: "SEO & TECHNIQUE",
    icon: Globe,
    items: [
      { id: "domain_configured", label: "Domaine mutagim.shop configuré", critical: true },
      { id: "ssl_active", label: "SSL certificat actif", critical: true },
      { id: "search_console", label: "Google Search Console vérifié", critical: false },
      { id: "sitemap_submitted", label: "Sitemap soumis à Google", critical: false },
      { id: "sentry_dsn", label: "Sentry DSN configuré", critical: false },
    ]
  },
  {
    category: "LÉGAL",
    icon: Shield,
    items: [
      { id: "cgv_published", label: "CGV rédigées et publiées", critical: true },
      { id: "privacy_policy", label: "Politique confidentialité publiée", critical: true },
      { id: "legal_mentions", label: "Mentions légales publiées", critical: true },
    ]
  },
];

export function AdminProductionChecklistPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState('');
  const [checkedItems, setCheckedItems] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchChecklist(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchChecklist = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/settings/production-checklist`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCheckedItems(data.checked_items || {});
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (itemId) => {
    const newChecked = { ...checkedItems, [itemId]: !checkedItems[itemId] };
    setCheckedItems(newChecked);
    
    // Sauvegarder automatiquement
    setIsSaving(true);
    try {
      await fetch(`${API_URL}/api/settings/production-checklist`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ checked_items: newChecked })
      });
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Calculs de progression
  const totalItems = CHECKLIST_ITEMS.flatMap(c => c.items).length;
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const criticalItems = CHECKLIST_ITEMS.flatMap(c => c.items.filter(i => i.critical));
  const criticalChecked = criticalItems.filter(i => checkedItems[i.id]).length;
  const progressPercent = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;
  const isReadyForProduction = criticalItems.every(i => checkedItems[i.id]);

  const getStatusIcon = (checked, critical) => {
    if (checked) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (critical) return <XCircle className="h-5 w-5 text-red-500" />;
    return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
  };

  if (isLoading) {
    return (
      <AdminPageWrapper title="Checklist Production" subtitle="Chargement...">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-mutagim-red border-t-transparent rounded-full" />
        </div>
      </AdminPageWrapper>
    );
  }

  return (
    <AdminPageWrapper 
      title="Checklist Production" 
      subtitle={`${checkedCount}/${totalItems} éléments configurés`}
    >
      <div className="space-y-8" data-testid="production-checklist-page">
        {/* Progress Overview */}
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-500">Progression globale</p>
                <p className="text-2xl font-bold">{Math.round(progressPercent)}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Éléments critiques</p>
                <p className={`text-2xl font-bold ${criticalChecked === criticalItems.length ? 'text-green-600' : 'text-red-600'}`}>
                  {criticalChecked}/{criticalItems.length}
                </p>
              </div>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </CardContent>
        </Card>

        {/* Checklist Categories */}
        <div className="space-y-6">
          {CHECKLIST_ITEMS.map(category => {
            const CategoryIcon = category.icon;
            const categoryChecked = category.items.filter(i => checkedItems[i.id]).length;
            
            return (
              <Card key={category.category}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CategoryIcon className="h-5 w-5 text-mutagim-red" />
                      {category.category}
                    </CardTitle>
                    <Badge variant="outline">
                      {categoryChecked}/{category.items.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {category.items.map(item => (
                      <div 
                        key={item.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          checkedItems[item.id] 
                            ? 'bg-green-50 border-green-200' 
                            : item.critical 
                              ? 'bg-red-50 border-red-200' 
                              : 'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={item.id}
                            checked={checkedItems[item.id] || false}
                            onCheckedChange={() => handleToggle(item.id)}
                            data-testid={`check-${item.id}`}
                          />
                          <label 
                            htmlFor={item.id}
                            className={`text-sm cursor-pointer ${
                              checkedItems[item.id] ? 'line-through text-gray-500' : ''
                            }`}
                          >
                            {item.label}
                          </label>
                          {item.critical && !checkedItems[item.id] && (
                            <Badge variant="destructive" className="text-xs">
                              Critique
                            </Badge>
                          )}
                        </div>
                        {getStatusIcon(checkedItems[item.id], item.critical)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Action Button */}
        {isReadyForProduction && (
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 p-4 bg-green-100 rounded-lg">
              <Zap className="h-6 w-6 text-green-600" />
              <span className="text-green-800 font-medium">
                Tous les éléments critiques sont configurés. Vous êtes prêt à lancer !
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminProductionChecklistPage;
